import { lazy, Suspense, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Sparkles, Users, FileText, CreditCard, Bell, Wallet, Download, Calculator, ExternalLink, Star, Menu } from "lucide-react";
import taskeerLogo from "@/assets/taskeerlogo.png";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/landing/ScrollReveal";
import LiveCounter from "@/components/landing/LiveCounter";
import HeroBackground from "@/components/landing/HeroBackground";
import OfferBanner from "@/components/landing/OfferBanner";
import LanguageSelector from "@/components/landing/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { PRICE_DISPLAY } from "@/hooks/useSubscription";
import { MobileNav } from "@/components/MobileNav";

// Lazy load sections below the fold
const BlogPreview = lazy(() => import("@/components/BlogPreview").then(m => ({
  default: m.BlogPreview
})));
const HowItWorksSection = lazy(() => import("@/components/landing/HowItWorksSection").then(m => ({
  default: m.HowItWorksSection
})));
const PainPointsSection = lazy(() => import("@/components/landing/PainPointsSection").then(m => ({
  default: m.PainPointsSection
})));
const TestimonialsSection = lazy(() => import("@/components/landing/TestimonialsSection"));
const FAQSection = lazy(() => import("@/components/landing/FAQSection"));
const FinalCTASection = lazy(() => import("@/components/landing/FinalCTASection"));

// Minimal loading fallback
const SectionSkeleton = () => <div className="w-full py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>;

const Landing = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const mobileMenuItems = [
    { icon: Bell, label: t('header.news'), path: "/updates" },
    { icon: Download, label: t('installApp'), path: "/install" },
  ];
  
  // Display prices based on selected language (PT = BRL, EN/ES = USD)
  const prices = useMemo(() => {
    const currency = language === 'pt' ? 'brl' : 'usd';
    return PRICE_DISPLAY[currency];
  }, [language]);
  
  // Apenas os pontos mais fortes da plataforma
  const features = [{
    icon: <FileText className="w-6 h-6" />,
    title: t('features.projectManagement'),
    description: t('features.projectManagementDesc')
  }, {
    icon: <CreditCard className="w-6 h-6" />,
    title: t('features.pixCharges'),
    description: t('features.pixChargesDesc')
  }, {
    icon: <ExternalLink className="w-6 h-6" />,
    title: t('features.clientPortal'),
    description: t('features.clientPortalDesc')
  }, {
    icon: <Calculator className="w-6 h-6" />,
    title: t('features.budgets'),
    description: t('features.budgetsDesc')
  }, {
    icon: <Wallet className="w-6 h-6" />,
    title: t('features.finance'),
    description: t('features.financeDesc')
  }, {
    icon: <Users className="w-6 h-6" />,
    title: t('features.teamCollab'),
    description: t('features.teamCollabDesc')
  }];
  
  return <div className="min-h-screen bg-background">
      {/* Offer Banner */}
      <OfferBanner />

      {/* Header */}
      <header className="border-b border-border backdrop-blur-xl bg-background/80 sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center">
          <img src={taskeerLogo} alt="Taskeer" className="h-6 sm:h-8 w-auto object-contain" loading="eager" width="136" height="24" />
          
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" size="icon" onClick={() => navigate("/updates")} className="text-primary" title={t('header.news')}>
              <Bell className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/install")} className="text-primary" title={t('installApp')}>
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/auth")} className="hover-glow text-sm sm:text-base">
              {t('header.enter')}
            </Button>
          </div>
          
          {/* Mobile Navigation */}
          <div className="flex sm:hidden items-center gap-2">
            <LanguageSelector />
            <Button variant="outline" size="sm" onClick={() => navigate("/auth")} className="hover-glow">
              {t('header.enter')}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Mobile Menu Drawer */}
      <MobileNav
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        menuItems={mobileMenuItems}
      />

      {/* Hero Section */}
      <section className="relative min-h-[58vh] sm:min-h-[65vh] flex items-center overflow-hidden">
        {/* Animated Background */}
        <HeroBackground />
        
        <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-12 md:py-14 text-center relative z-10">
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-5 animate-fade-in">
            <div className="inline-block">
              <span className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full glass text-xs sm:text-sm font-medium border border-primary/20">
                {t('hero.badge')}
              </span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight px-2">
              {t('hero.title1')}
              <span className="text-prime block mt-2 bg-gradient-to-r from-primary via-blue-400 to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                {t('hero.title2')}
              </span>
              {t('hero.title3')}
            </h2>
            
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center pt-2 sm:pt-4 px-4">
              <Button size="lg" className="prime-gradient hover-glow text-base sm:text-lg px-6 py-5 sm:px-8 sm:py-6 shadow-prime w-full sm:w-auto group" onClick={() => navigate("/auth")}>
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:rotate-12 transition-transform" />
                {t('hero.cta')}
              </Button>
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 py-5 sm:px-8 sm:py-6 hover-glow w-full sm:w-auto backdrop-blur-sm">
                {t('hero.demo')}
              </Button>
            </div>

            {/* Plano gratuito em destaque */}
            <p className="flex items-center justify-center gap-2 text-sm sm:text-base text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
              {t('hero.freeNote')}
            </p>

            {/* Live Counter */}
            <div className="pt-4 sm:pt-6">
              <LiveCounter />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <ScrollReveal>
        <Suspense fallback={<SectionSkeleton />}>
          <PainPointsSection />
        </Suspense>
      </ScrollReveal>

      {/* How It Works Section */}
      <ScrollReveal delay={100}>
        <Suspense fallback={<SectionSkeleton />}>
          <HowItWorksSection />
        </Suspense>
      </ScrollReveal>

      {/* Testimonials Section */}
      <ScrollReveal delay={100}>
        <Suspense fallback={<SectionSkeleton />}>
          <TestimonialsSection />
        </Suspense>
      </ScrollReveal>

      {/* Features Grid */}
      <ScrollReveal delay={100}>
        <section className="container mx-auto px-3 sm:px-4 py-10 sm:py-16 md:py-20">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-4">
              {t('features.title')}
            </h3>
            <p className="text-muted-foreground text-base sm:text-lg px-4">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => <ScrollReveal key={index} delay={index * 50} direction="up">
                <Card className="p-4 sm:p-6 glass hover-glow transition-all duration-300 h-full">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3 sm:mb-4 text-primary">
                    {feature.icon}
                  </div>
                  <h4 className="text-lg sm:text-xl font-semibold mb-2">{feature.title}</h4>
                  <p className="text-sm sm:text-base text-muted-foreground">{feature.description}</p>
                </Card>
              </ScrollReveal>)}
          </div>
        </section>
      </ScrollReveal>

      {/* Pricing Section */}
      <ScrollReveal delay={100}>
        <section className="container mx-auto px-3 sm:px-4 py-10 sm:py-14">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-4">
              {t('pricing.title')}
            </h3>
            <p className="text-muted-foreground text-base sm:text-lg px-4">
              {t('pricing.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto items-stretch">
            {/* Free Plan */}
            <ScrollReveal delay={50} direction="up">
              <Card className="glass p-1 hover-glow transition-all duration-300 h-full">
                <div className="bg-card rounded-lg p-6 sm:p-8 text-center space-y-5 h-full flex flex-col">
                  <div className="inline-flex items-center justify-center gap-2 text-xl font-bold">
                    <span>{t('pricing.free.name')}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium uppercase tracking-wide">
                      {t('pricing.free.badge')}
                    </span>
                  </div>

                  <div className="text-4xl font-bold">
                    {t('pricing.free.price')}<span className="text-lg text-muted-foreground">{t('pricing.month')}</span>
                  </div>

                  <ul className="space-y-3 text-left flex-1">
                    {['pricing.free.projects', 'pricing.free.client', 'pricing.free.structure', 'pricing.free.tools', 'pricing.free.noCard'].map(key => (
                      <li key={key} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{t(key)}</span>
                      </li>
                    ))}
                  </ul>

                  <Button size="lg" variant="outline" className="w-full hover-glow" onClick={() => navigate("/auth")}>
                    {t('pricing.free.cta')}
                  </Button>
                </div>
              </Card>
            </ScrollReveal>

            {/* Prime Plan */}
            <ScrollReveal delay={100} direction="up">
              <Card className="glass p-1 hover-glow transition-all duration-300 h-full">
                <div className="bg-card rounded-lg p-8 text-center space-y-6 px-[32px] my-0 py-[46px]">
                  <div className="inline-flex items-center gap-2 text-xl font-bold">
                    <Sparkles className="w-6 h-6 text-primary" />
                    <span className="text-primary">Taskeer Prime</span>
                  </div>
                  
                  <div className="text-4xl font-bold">
                    {prices.prime.formatted}<span className="text-lg text-muted-foreground">{t('pricing.month')}</span>
                  </div>

                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.prime.projects')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.prime.members')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.prime.charges')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.prime.schedule')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.prime.budgets')}</span>
                    </li>
                  </ul>

                  <Button size="lg" variant="outline" className="w-full hover-glow" onClick={() => navigate("/auth")}>
                    {t('pricing.prime.cta')}
                  </Button>
                </div>
              </Card>
            </ScrollReveal>

            {/* PRO Plan */}
            <ScrollReveal delay={200} direction="up">
              <Card className="glass prime-gradient p-1 glow-prime relative h-full">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-1 bg-blue-700 text-white rounded-full text-sm font-medium">
                    {t('pricing.recommended')}
                  </span>
                </div>
                <div className="bg-card rounded-lg p-8 text-center space-y-6">
                  <div className="inline-flex items-center gap-2 text-xl font-bold">
                    <Sparkles className="w-6 h-6 text-primary" />
                    <span className="text-primary">Taskeer PRO</span>
                  </div>
                  
                  <div className="text-4xl font-bold">
                    {prices.pro.formatted}<span className="text-lg text-muted-foreground">{t('pricing.month')}</span>
                  </div>

                  <ul className="space-y-2 text-left text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.pro.projects')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t('pricing.pro.members')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="flex items-center gap-2">
                        {t('pricing.pro.customization')}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium">{t('pricing.exclusive')}</span>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="flex items-center gap-2">
                        {t('pricing.pro.clients')}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium">{t('pricing.exclusive')}</span>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="flex items-center gap-2">
                        {t('pricing.pro.dashboard')}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium">{t('pricing.exclusive')}</span>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="flex items-center gap-2">
                        {t('pricing.pro.billing')}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium">{t('pricing.exclusive')}</span>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="flex items-center gap-2">
                        {t('pricing.pro.export')}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium">{t('pricing.exclusive')}</span>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-muted-foreground italic">{t('pricing.pro.andMore')}</span>
                    </li>
                  </ul>

                  <Button size="lg" className="w-full prime-gradient hover-glow shadow-prime" onClick={() => navigate("/auth")}>
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('pricing.pro.cta')}
                  </Button>
                </div>
              </Card>
            </ScrollReveal>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            {t('pricing.noClientSub')}
          </p>
        </section>
      </ScrollReveal>

      {/* FAQ Section */}
      <ScrollReveal delay={100}>
        <Suspense fallback={<SectionSkeleton />}>
          <FAQSection />
        </Suspense>
      </ScrollReveal>

      {/* Blog Preview Section */}
      <ScrollReveal delay={100}>
        <Suspense fallback={<SectionSkeleton />}>
          <BlogPreview />
        </Suspense>
      </ScrollReveal>

      {/* Link para novidades */}
      <div className="container mx-auto px-3 sm:px-4 pb-4 text-center">
        <Button variant="ghost" onClick={() => navigate("/updates")} className="text-muted-foreground hover:text-primary">
          <Bell className="w-4 h-4 mr-2" />
          {t('updates.viewAll')}
        </Button>
      </div>


      {/* Final CTA Section */}
      <ScrollReveal delay={100}>
        <Suspense fallback={<SectionSkeleton />}>
          <FinalCTASection />
        </Suspense>
      </ScrollReveal>

      <Footer />
    </div>;
};

export default Landing;
